import Channel, { ThreadChannel } from '@@types/Channel';
import { CHANNEL_ID } from '@@types/common';
import Log from '@@types/Log';

import { MAIN_THREAD_CHANNEL_ID } from '~/constants';
import { isArchivedChannel, isBotChannel, isDirectChannel, isSharedChannel } from '~/services/channelService';
import Actions from '~/stores/Actions';
import { channelMailSettingStore } from '~/stores/ChannelMailSettingStore';
import { channelManager } from '~/stores/ChannelManager';
import { channelsStore } from '~/stores/ChannelsStore';
import { mainDetailUiState } from '~/stores/DetailUiState';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { stores } from '~/stores/Stores';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { isInEditableTime } from '~/utils/channelLogsUtil';
import { isNonNil } from '~/utils/fnUtil/fnUtil';
import { isLeaver } from '~/utils/memberUtil';
import { computed } from 'mobx';
import { propOr } from 'ramda';

class PermissionActions {
  constructor(private actions: Actions) {}

  @computed
  get hasContents(): boolean {
    return isNonNil(this.contentsChannel);
  }

  @computed
  private get isSharedContentsChannel(): boolean {
    return isSharedChannel(this.contentsChannel);
  }

  @computed
  private get isMainThreadChannel(): boolean {
    return this.contentsChannelId === MAIN_THREAD_CHANNEL_ID;
  }

  @computed
  private get isBotChannel(): boolean {
    return isBotChannel(this.contentsChannel);
  }

  private get isMainView(): boolean {
    return uiState.isMainView;
  }

  private get contentsChannel() {
    return stores.contentsChannel;
  }

  private get contentsChannelId() {
    return mainDetailUiState.contentsChannelId;
  }

  private get isLiteVersion() {
    return globalSettingStore.isLiteVersion;
  }

  // 에디터

  get canUseEditor(): boolean {
    return !this.isBotChannel && !this.isMainThreadChannel;
  }

  get canUseMeeting(): boolean {
    return globalSettingStore.systemCommands?.roundee && !this.isSharedContentsChannel;
  }

  get canUseWebex(): boolean {
    return globalSettingStore.systemCommands?.webex && !this.isSharedContentsChannel;
  }

  get canUseCommand(): boolean {
    return !this.isSharedContentsChannel;
  }

  get canUseSticker(): boolean {
    return !this.isSharedContentsChannel;
  }

  get showInterTenantDescription(): boolean {
    return this.isSharedContentsChannel;
  }

  // 본문 상단
  get showOpponentStatus(): boolean {
    return this.hasContents && stores.isDirectContentsChannel;
  }

  get showFavoriteBtn(): boolean {
    return (
      !this.isMainThreadChannel &&
      !this.isSharedContentsChannel &&
      !stores.isDoorayNewsChannelId(this.contentsChannelId)
    );
  }

  get showPopupBtn(): boolean {
    return this.hasContents && this.isMainView;
  }

  get showSettingBtn(): boolean {
    return !this.isMainThreadChannel && !this.isBotChannel && this.hasContents;
  }

  get showNotificationBtn(): boolean {
    return !this.isMainThreadChannel;
  }

  // 본문
  get showLogMenuPopper(): boolean {
    return !uiState.isDeletingChannel && !this.isSharedContentsChannel;
  }

  get showNotInWork(): boolean {
    return !this.isSharedContentsChannel;
  }

  // 멤버 설정
  get showCreateNewChannelBtn(): boolean {
    return this.isMainView && !userStore.isGuest && !this.isSharedContentsChannel;
  }

  // 채널 설정
  get showTranslationSetting(): boolean {
    return !this.isLiteVersion && globalSettingStore.translator.enabled && !this.isSharedContentsChannel;
  }

  get showAdminOnlySetting(): boolean {
    return stores.isPrivateContentsChannel && !this.isSharedContentsChannel;
  }

  get showShareChannelSetting(): boolean {
    return this.isSharedContentsChannel;
  }

  get showChannelMailSetting(): boolean {
    const channelMailSetting = channelMailSettingStore.getMailSetting(this.contentsChannelId);
    return propOr(false, 'enabled', channelMailSetting) && !this.isLiteVersion && !this.isSharedContentsChannel;
  }

  get showSlashCommandSetting(): boolean {
    return !this.isSharedContentsChannel;
  }

  get showIncomingHookSetting(): boolean {
    return !this.isSharedContentsChannel;
  }

  get showArchiveSetting(): boolean {
    return stores.isPrivateContentsChannel && !userStore.isGuest && !this.isSharedContentsChannel;
  }

  // 우측 버튼들
  get canUseMenuTrigger(): boolean {
    return !this.isBotChannel;
  }

  get showLogPanel(): boolean {
    return this.isMainView && !this.isSharedContentsChannel;
  }

  get showNoticePanel(): boolean {
    return !this.isSharedContentsChannel;
  }

  get showMentionPanel(): boolean {
    return this.isMainView && !this.isSharedContentsChannel;
  }

  get showFilePanel(): boolean {
    return this.isMainView && !this.isSharedContentsChannel;
  }

  get showLinkPanel(): boolean {
    return this.isMainView && !this.isSharedContentsChannel;
  }

  get showSideApps(): boolean {
    return !this.isLiteVersion;
  }

  isLeaverDirectChannel = (channel: Channel): boolean => {
    if (!isDirectChannel(channel)) {
      return false;
    }
    const member = interTenantMembersStore.getById(channel.opponentId);
    return member && isLeaver(member);
  };

  isReadonlyChannel = (channel: Channel | ThreadChannel | undefined): boolean => {
    if (!channel) {
      return false;
    }
    channel = channelManager.getMainChannel(channel.channelId);
    return isBotChannel(channel) || isArchivedChannel(channel) || this.isLeaverDirectChannel(channel);
  };

  isReadonlyChannelId = (channelId: CHANNEL_ID): boolean => {
    const channel = channelManager.getById(channelId);
    return channel && this.isReadonlyChannel(channel);
  };

  public canEditOrDeleteLog = (log: Log) => {
    const { messageDeletablePeriodMinutes } = globalSettingStore.messengerGeneralSetting;
    return (
      this.actions.menuActions.canEditOrDeleteLog(log) &&
      (channelsStore.meChannel?.channelId === channelManager.getMainChannelId(log.channelId) ||
        isInEditableTime(messageDeletablePeriodMinutes, log))
    );
  };
}

export default PermissionActions;
